import { useCallback, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/cn';
import { useNotifications } from '../../store/useStore';
import { emailService } from '../../lib/email/emailService';
import { listeService } from '../../lib/email/listeService';
import { SceltaBaseConsenso } from './SceltaBaseConsenso';
import {
  costruisciRighe,
  indovinaColonne,
  leggiFile,
  mappaVuota,
  type CampoContatto,
  type FileLetto,
  type MappaColonne,
} from '../../lib/email/importContacts';
import {
  EMAIL_IMPORT_BASIS,
  type EmailImportBasis,
  type EmailImportResult,
  type EmailLista,
} from '../../types/email.types';

interface ImportContactsModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  /** Le liste in cui si possono mettere subito i contatti importati. */
  liste: EmailLista[];
}

type Passo = 'file' | 'colonne' | 'provenienza' | 'esito';

const CAMPI: { chiave: CampoContatto; etichetta: string; obbligatorio?: boolean }[] = [
  { chiave: 'email', etichetta: 'Indirizzo email', obbligatorio: true },
  { chiave: 'nome', etichetta: 'Nome' },
  { chiave: 'azienda', etichetta: 'Azienda' },
  { chiave: 'telefono', etichetta: 'Telefono' },
  { chiave: 'citta', etichetta: 'Citta' },
  { chiave: 'provincia', etichetta: 'Provincia' },
];

const OGGI = () => new Date().toISOString().slice(0, 10);

export function ImportContactsModal({ open, onClose, onImported, liste }: ImportContactsModalProps) {
  const { addNotification } = useNotifications();

  const [passo, setPasso] = useState<Passo>('file');
  const [nomeFile, setNomeFile] = useState('');
  const [letto, setLetto] = useState<FileLetto | null>(null);
  const [mappa, setMappa] = useState<MappaColonne>(mappaVuota());
  const [leggendo, setLeggendo] = useState(false);

  const [fonte, setFonte] = useState('');
  const [base, setBase] = useState<EmailImportBasis | null>(null);
  const [dataConsenso, setDataConsenso] = useState(OGGI);
  const [note, setNote] = useState('');
  const [listaId, setListaId] = useState('');

  const [importando, setImportando] = useState(false);
  const [esito, setEsito] = useState<EmailImportResult | null>(null);
  const [messiInLista, setMessiInLista] = useState<number | null>(null);

  const righe = useMemo(
    () => (letto ? costruisciRighe(letto, mappa) : null),
    [letto, mappa]
  );

  const azzera = useCallback(() => {
    setPasso('file');
    setNomeFile('');
    setLetto(null);
    setMappa(mappaVuota());
    setFonte('');
    setBase(null);
    setDataConsenso(OGGI());
    setNote('');
    setListaId('');
    setEsito(null);
    setMessiInLista(null);
  }, []);

  const chiudi = useCallback(() => {
    azzera();
    onClose();
  }, [azzera, onClose]);

  const scegliFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      setLeggendo(true);
      try {
        const contenuto = await leggiFile(file);
        if (contenuto.righe.length === 0) {
          addNotification({ type: 'error', title: 'File vuoto', message: 'Non ho trovato nessuna riga da leggere.' });
          return;
        }
        setNomeFile(file.name);
        setLetto(contenuto);
        setMappa(indovinaColonne(contenuto));
        setPasso('colonne');
      } catch (errore) {
        addNotification({
          type: 'error',
          title: 'Non riesco a leggere il file',
          message: errore instanceof Error ? errore.message : 'Formato non riconosciuto.',
        });
      } finally {
        setLeggendo(false);
      }
    },
    [addNotification]
  );

  const importa = useCallback(async () => {
    if (!righe || !base || !fonte.trim()) return;
    setImportando(true);
    try {
      const risultato = await emailService.importContacts(
        righe.valide,
        fonte.trim(),
        base,
        new Date(dataConsenso),
        note.trim() || undefined
      );
      // Anche gli indirizzi bloccati finiscono nella lista: non cambia niente,
      // tanto non ricevono, e la lista resta fedele al file importato.
      if (listaId) {
        setMessiInLista(
          await listeService.aggiungiPerIndirizzo(listaId, righe.valide.map((r) => r.email))
        );
      }
      setEsito(risultato);
      setPasso('esito');
      onImported();
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Importazione non riuscita',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    } finally {
      setImportando(false);
    }
  }, [righe, base, fonte, dataConsenso, note, listaId, onImported, addNotification]);

  const baseScelta = EMAIL_IMPORT_BASIS.find((b) => b.value === base);
  const puoImportare = Boolean(righe?.valide.length && fonte.trim() && base && dataConsenso);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && chiudi()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importa contatti</DialogTitle>
          <DialogDescription>
            {passo === 'file' && 'Carica un file CSV o Excel con gli indirizzi.'}
            {passo === 'colonne' && 'Controlla che le colonne siano state riconosciute bene.'}
            {passo === 'provenienza' && 'Da dove arrivano questi contatti. Senza questa parte non si puo\' importare.'}
            {passo === 'esito' && 'Ecco com\'e\' andata.'}
          </DialogDescription>
        </DialogHeader>

        {/* ---------------------------------------------------------------- */}
        {passo === 'file' && (
          <div className="py-4">
            <label
              className={cn(
                'flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center cursor-pointer transition-colors',
                'hover:border-primary-400 hover:bg-primary-50/40'
              )}
            >
              {leggendo ? (
                <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
              ) : (
                <Upload className="w-10 h-10 text-neutral-400" />
              )}
              <div>
                <p className="font-medium text-gray-900">Scegli un file</p>
                <p className="text-sm text-neutral-500 mt-1">CSV, XLSX o XLS</p>
              </div>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                disabled={leggendo}
                onChange={(e) => scegliFile(e.target.files?.[0])}
              />
            </label>
            <p className="text-xs text-neutral-500 mt-4">
              Serve almeno una colonna con gli indirizzi email. Nome, azienda, telefono, citta e provincia
              sono facoltativi: se ci sono, li riconosco da solo.
            </p>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {passo === 'colonne' && letto && righe && (
          <div className="py-2 space-y-4">
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <FileSpreadsheet className="w-4 h-4 text-neutral-400" />
              <span className="font-medium text-gray-900">{nomeFile}</span>
              <span className="text-neutral-400">·</span>
              <span>{letto.righe.length} righe</span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {CAMPI.map((campo) => (
                <div key={campo.chiave}>
                  <Label className="text-xs">
                    {campo.etichetta}
                    {campo.obbligatorio && <span className="text-primary-600 ml-1">*</span>}
                  </Label>
                  <select
                    className="mt-1 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={mappa[campo.chiave] ?? ''}
                    onChange={(e) =>
                      setMappa((m) => ({ ...m, [campo.chiave]: e.target.value || null }))
                    }
                  >
                    <option value="">— non presente —</option>
                    {letto.intestazioni.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span className="text-green-700 font-medium">{righe.valide.length} da importare</span>
                {righe.duplicati > 0 && (
                  <span className="text-neutral-600">{righe.duplicati} doppioni nel file</span>
                )}
                {righe.scartate.length > 0 && (
                  <span className="text-orange-700">{righe.scartate.length} scartate</span>
                )}
              </div>
              {righe.scartate.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs text-neutral-600 max-h-24 overflow-y-auto">
                  {righe.scartate.slice(0, 8).map((s, i) => (
                    <li key={i}>
                      Riga {s.riga}: {s.motivo}
                      {s.valore && <span className="text-neutral-400"> ({s.valore})</span>}
                    </li>
                  ))}
                  {righe.scartate.length > 8 && (
                    <li className="text-neutral-400">… e altre {righe.scartate.length - 8}</li>
                  )}
                </ul>
              )}
            </div>

            {righe.valide.length > 0 && (
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-neutral-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Email</th>
                      <th className="text-left px-3 py-2 font-medium">Nome</th>
                      <th className="text-left px-3 py-2 font-medium">Azienda</th>
                    </tr>
                  </thead>
                  <tbody>
                    {righe.valide.slice(0, 3).map((r) => (
                      <tr key={r.email} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-900">{r.email}</td>
                        <td className="px-3 py-2 text-neutral-600">{r.nome || '—'}</td>
                        <td className="px-3 py-2 text-neutral-600">{r.azienda || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {passo === 'provenienza' && (
          <div className="py-2 space-y-5">
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900 leading-relaxed">
                Quello che scrivi qui resta registrato accanto a ogni contatto e non si puo' piu'
                modificare. E' la prova da mostrare se qualcuno contesta di aver ricevuto le mail.
              </p>
            </div>

            <div>
              <Label htmlFor="fonte">
                Da dove arriva questa lista <span className="text-primary-600">*</span>
              </Label>
              <Input
                id="fonte"
                value={fonte}
                onChange={(e) => setFonte(e.target.value)}
                placeholder="Es. Fiera Agrilevante 2026, moduli cartacei allo stand"
                className="mt-1"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Scrivilo come lo spiegheresti a voce: chi te li ha dati e in che occasione.
              </p>
            </div>

            <div>
              <Label>
                Perche' possiamo scrivergli <span className="text-primary-600">*</span>
              </Label>
              <div className="mt-2">
                <SceltaBaseConsenso valore={base} onScegli={setBase} />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="data-consenso">
                  Quando l'hanno dato <span className="text-primary-600">*</span>
                </Label>
                <Input
                  id="data-consenso"
                  type="date"
                  max={OGGI()}
                  value={dataConsenso}
                  onChange={(e) => setDataConsenso(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="note-import">Note (facoltative)</Label>
              <Textarea
                id="note-import"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Es. schede cartacee scansionate, archiviate in ufficio"
                className="mt-1"
              />
            </div>

            {liste.length > 0 && (
              <div>
                <Label htmlFor="lista-import">Mettili anche in una lista (facoltativo)</Label>
                <select
                  id="lista-import"
                  value={listaId}
                  onChange={(e) => setListaId(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">— nessuna lista —</option>
                  {liste.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-neutral-500 mt-1">
                  Cosi' puoi mandare una campagna solo a chi arriva da questo file.
                </p>
              </div>
            )}

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-neutral-700">
              Stai per importare <strong>{righe?.valide.length ?? 0}</strong> contatti
              {baseScelta && <> come <strong>{baseScelta.label.toLowerCase()}</strong></>}.
            </div>
          </div>
        )}

        {/* ---------------------------------------------------------------- */}
        {passo === 'esito' && esito && (
          <div className="py-4 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
              <div>
                <p className="font-semibold text-gray-900">Importazione completata</p>
                <p className="text-sm text-neutral-500">{nomeFile}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-2xl font-bold text-green-600">{esito.nuovi}</p>
                <p className="text-xs text-neutral-500 uppercase tracking-wider">Nuovi</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-2xl font-bold text-gray-900">{esito.aggiornati}</p>
                <p className="text-xs text-neutral-500 uppercase tracking-wider">Gia' presenti</p>
              </div>
              {esito.bloccati > 0 && (
                <div className="rounded-lg border border-neutral-300 bg-neutral-50 p-3">
                  <p className="text-2xl font-bold text-neutral-700">{esito.bloccati}</p>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Bloccati</p>
                </div>
              )}
              {esito.scartati > 0 && (
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                  <p className="text-2xl font-bold text-orange-700">{esito.scartati}</p>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Scartati</p>
                </div>
              )}
            </div>

            {esito.bloccati > 0 && (
              <p className="text-xs text-neutral-600 leading-relaxed">
                I contatti bloccati si erano gia' disiscritti o avevano segnalato le nostre mail come
                spam: sono stati aggiornati nei dati ma restano fuori da ogni invio.
              </p>
            )}

            {messiInLista !== null && (
              <p className="text-xs text-neutral-600 leading-relaxed">
                <strong>{messiInLista}</strong> contatti messi nella lista{' '}
                <strong>{liste.find((l) => l.id === listaId)?.name}</strong>.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {passo === 'colonne' && (
            <>
              <Button variant="outline" onClick={() => setPasso('file')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Cambia file
              </Button>
              <Button
                onClick={() => setPasso('provenienza')}
                disabled={!righe?.valide.length}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                Avanti
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {passo === 'provenienza' && (
            <>
              <Button variant="outline" onClick={() => setPasso('colonne')} disabled={importando}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
              <Button
                onClick={importa}
                disabled={!puoImportare || importando}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                {importando ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importo…
                  </>
                ) : (
                  <>Importa {righe?.valide.length ?? 0} contatti</>
                )}
              </Button>
            </>
          )}

          {passo === 'esito' && (
            <>
              <Button variant="outline" onClick={azzera}>
                Importa un altro file
              </Button>
              <Button onClick={chiudi} className="bg-primary-600 hover:bg-primary-700 text-white">
                Chiudi
              </Button>
            </>
          )}

          {passo === 'file' && (
            <Button variant="outline" onClick={chiudi}>
              Annulla
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
